import { ActivityIndicator, ScrollView, View } from "react-native";
import React, { useCallback, useEffect } from "react";
import { useFocusEffect } from "@react-navigation/native";
import { useQuery } from "@tanstack/react-query";
import { router, Slot, usePathname } from "expo-router";
import { UnderlineTabBar } from "@/components/ui/UnderlineTabBar";
import UnlinkedMyMoney from "@/components/myMoney/UnlinkedMyMoney";
import { StyleSheet } from "@/utils/appStyleSheet";
import { Colors } from "@/constants/Colors";
import useFinanceDataStore from "@/store/financeData";
import useWindow from "@/hooks/useWindow";
import SubscriptionServices from "@/services/subscriptionServices";
import { SubscriptionSetupType } from "../../my-money/setup";

type TabKey =
  | "index"
  | "spending"
  | "income"
  | "transactions"
  | "account"
  | "budget";

const TAB_OPTIONS = [
  { key: "index" as TabKey, label: "Dashboard", emoji: "💰" },
  { key: "spending" as TabKey, label: "Spending", emoji: "📉" },
  { key: "income" as TabKey, label: "Income", emoji: "💵" },
  { key: "transactions" as TabKey, label: "Transactions", emoji: "📄" },
  { key: "account" as TabKey, label: "Accounts", emoji: "💳" },
  { key: "budget" as TabKey, label: "Budget", emoji: "🎯" },
];

const TAB_PATHS: Record<TabKey, string> = {
  index: "/(tabs)/my-money",
  spending: "/(tabs)/my-money/spending",
  income: "/(tabs)/my-money/income",
  transactions: "/(tabs)/my-money/transactions",
  account: "/(tabs)/my-money/account",
  budget: "/(tabs)/my-money/budget",
};

export default function MyMoneyLayout() {
  const pathname = usePathname();
  const { isDesktop } = useWindow();
  const { fetchFinanceData, isLoading: isFinanceDataLoading } =
    useFinanceDataStore();

  const { data: financeAccounts, isLoading } = useQuery({
    queryKey: ["financeAccounts"],
    queryFn: SubscriptionServices.getListAccounts,
  });

  const isLinkedAccounts = (financeAccounts?.data?.data?.length || 0) > 0;

  useFocusEffect(
    useCallback(() => {
      if (isLinkedAccounts) {
        fetchFinanceData({ silent: true });
      }
    }, [isLinkedAccounts, fetchFinanceData]),
  );

  useEffect(() => {
    if (isLinkedAccounts) {
      fetchFinanceData();
    }
  }, [isLinkedAccounts, fetchFinanceData]);

  const handleConnect = () => {
    router.push({
      pathname: "/my-money/setup",
      params: {
        type: SubscriptionSetupType.MyMoney,
      },
    });
  };

  const getActiveTab = (): TabKey => {
    if (pathname === "/(tabs)/my-money" || pathname === "/my-money") {
      return "index";
    }
    const segments = pathname.split("/");
    const lastSegment = segments[segments.length - 1];
    // Map segment to tab key
    if (
      lastSegment === "spending" ||
      lastSegment === "income" ||
      lastSegment === "transactions" ||
      lastSegment === "account" ||
      lastSegment === "budget"
    ) {
      return lastSegment as TabKey;
    }
    return "index";
  };

  const activeTab = getActiveTab();
  const handleTabSelect = useCallback((key: string) => {
    router.replace(TAB_PATHS[key as TabKey] as any);
  }, []);

  if (isLoading || isFinanceDataLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Colors.primary} />
      </View>
    );
  }

  if (!isLinkedAccounts) {
    return <UnlinkedMyMoney onConnect={handleConnect} />;
  }

  return (
    <View style={styles.container}>
      <View
        style={[styles.tabBarContainer, isDesktop && styles.desktopCentered]}
      >
        <UnderlineTabBar
          tabs={TAB_OPTIONS}
          activeKey={activeTab}
          onTabSelect={handleTabSelect}
        />
      </View>

      <ScrollView
        style={[styles.content, isDesktop && styles.desktopCentered]}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <Slot />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.background,
  },
  tabBarContainer: {
    width: "100%",
  },
  desktopCentered: {
    maxWidth: 1200,
    width: "100%",
    alignSelf: "center",
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
});
